import React from 'react';
import { Link } from 'react-router-dom';

const Button = ({
  variant = 'primary',
  size = 'medium',
  children,
  className = '',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  href,
  to,
  fullWidth = false,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed';

  // Variant classes - with size-specific overrides for better control
  const getVariantClasses = (variant, size) => {
    if (variant === 'primary') {
      if (size === 'small') {
        return 'bg-primary-500 hover:bg-primary-600 active:scale-[0.98] text-white font-medium py-1.5 px-3 text-sm rounded-lg shadow-sm hover:shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50';
      }
      return 'bg-primary-500 hover:bg-primary-600 active:scale-[0.98] text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50';
    }
    if (variant === 'secondary') {
      if (size === 'small') {
        return 'bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 border border-slate-200 font-medium py-1.5 px-3 text-sm rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-200';
      }
      return 'bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 border border-slate-200 font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-200';
    }
    if (variant === 'accent') {
      if (size === 'small') {
        return 'bg-accent-500 hover:bg-accent-600 active:scale-[0.98] text-white font-medium py-1.5 px-3 text-sm rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-500/50';
      }
      return 'bg-accent-500 hover:bg-accent-600 active:scale-[0.98] text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-500/50';
    }

    const baseVariants = {
      danger: 'bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white rounded-xl shadow-sm transition-all focus:ring-red-500/50 py-2 px-4',
      ghost: 'bg-transparent hover:bg-slate-100/80 active:scale-[0.98] text-slate-600 hover:text-slate-900 rounded-lg transition-all focus:ring-slate-200 py-2 px-4',
      outline: 'bg-transparent border-2 border-primary-500 active:scale-[0.98] text-primary-600 hover:bg-primary-50 rounded-xl transition-all focus:ring-primary-500/50 py-2 px-4'
    };
    return baseVariants[variant] || baseVariants.primary;
  };

  // Size classes for non-CSS variants
  const sizeClasses = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-4',
    large: 'py-2.5 px-5 text-base'
  };

  const variantClass = getVariantClasses(variant, size);
  const needsSizeClass = !['primary', 'secondary', 'accent'].includes(variant) ||
    (size === 'small' && ['primary', 'secondary', 'accent'].includes(variant));
  const sizeClass = (needsSizeClass && !variantClass.includes('py-')) ? sizeClasses[size] : '';

  const fullWidthClass = fullWidth ? 'w-full' : '';

  const classes = `${baseClasses} ${variantClass} ${sizeClass} ${fullWidthClass} ${className}`;

  // Loading spinner
  const LoadingSpinner = () => (
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
  );

  // Icon rendering
  const renderIcon = () => {
    if (!icon) return null;
    return React.cloneElement(icon, {
      className: `h-4 w-4 ${iconPosition === 'left' ? 'mr-2' : 'ml-2'}`
    });
  };

  // Content with loading state
  const renderContent = () => (
    <>
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === 'left' && renderIcon()}
      {children}
      {!loading && icon && iconPosition === 'right' && renderIcon()}
    </>
  );

  // External link
  if (href) {
    return (
      <a
        href={href}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {renderContent()}
      </a>
    );
  }

  // React Router Link
  if (to) {
    return (
      <Link
        to={to}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {renderContent()}
      </Link>
    );
  }

  // Regular button
  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;